import React, { useState, useEffect } from 'react';
import { Modal, Box } from '@mui/material';
import { learningAssetAPI } from '../services/api';
import './TeachingAnalysisModal.css';

const TeachingAnalysisModal = ({ show, onHide, assetId, assetTitle }) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show && assetId) {
      fetchAnalysis();
    }
  }, [show, assetId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await learningAssetAPI.getTeachingAnalysis(assetId);
      setAnalysis(response.data.analysis);
    } catch (err) {
      // If no analysis exists yet, don't show error
      if (err.response?.status === 404) {
        setAnalysis(null);
      } else {
        setError(err.response?.data?.message || 'Failed to fetch teaching analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    try {
      setAnalyzing(true);
      setError('');
      const response = await learningAssetAPI.analyzeTeaching(assetId);
      setAnalysis(response.data.analysis);
    } catch (err) {
      console.error('Analysis error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to analyze teaching quality';
      setError(errorMsg);
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'success';
    if (score >= 70) return 'info';
    if (score >= 50) return 'warning';
    return 'danger';
  };

  const getScoreLabel = (score) => {
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Satisfactory';
    return 'Needs Improvement';
  };

  const formatDatetime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  if (!show) {
    return null;
  }

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="teaching-analysis-modal-title"
      disableScrollLock={true}
      BackdropProps={{ timeout: 0 }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '90%', md: '1200px' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 0,
          overflowY: 'auto',
          outline: 'none'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '2px solid #e2e8f0',
          position: 'sticky',
          top: 0,
          background: '#ffffff',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>🎓</span>
            <h2 id="teaching-analysis-modal-title" style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1a202c', fontFamily: 'inherit' }}>
              Instructor Teaching Quality Analysis
            </h2>
          </div>
          <button
            onClick={onHide}
            style={{
              padding: '8px',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#718096',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
        {error && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            background: '#fff5f5',
            border: '1px solid #fc8181',
            borderRadius: '8px',
            color: '#c53030',
            fontSize: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#c53030',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px'
              }}
            >
              ×
            </button>
          </div>
        )}

        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#718096' }}>
          Transcript: <strong style={{ color: '#1a202c' }}>{assetTitle}</strong>
        </p>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', textAlign: 'center' }}>
            <div className="spinner" style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '16px', fontSize: '14px', color: '#4a5568' }}>Loading analysis...</p>
          </div>
        ) : !analysis ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', textAlign: 'center' }}>
            <span style={{ fontSize: '64px', opacity: 0.3 }}>📊</span>
            <h3 style={{ margin: '20px 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>No Analysis Available</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#718096' }}>
              Run AI analysis to evaluate teaching quality
            </p>
            <button
              onClick={runAnalysis}
              disabled={analyzing}
              className="btn btn-primary"
              style={{
                fontSize: '14px',
                padding: '12px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: analyzing ? 0.6 : 1,
                cursor: analyzing ? 'not-allowed' : 'pointer'
              }}
            >
              {analyzing ? (
                <>
                  <span className="spinner" style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    display: 'inline-block'
                  }}></span>
                  Analyzing...
                </>
              ) : (
                <>
                  <span>🎓</span>
                  Run Teaching Analysis
                </>
              )}
            </button>
            <p style={{ margin: '16px 0 0 0', fontSize: '12px', color: '#a0aec0' }}>
              This will analyze the transcript using AI to evaluate teaching quality
            </p>
          </div>
        ) : (
          <div className="analysis-results">
            {/* Header with Overall Score */}
            <div style={{
              marginBottom: '24px',
              padding: '24px',
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ flex: '1 1 400px' }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>
                    Overall Teaching Score
                  </h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#718096' }}>
                    Analyzed on {formatDatetime(analysis.analyzedAt)}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div className={`score-circle score-${getScoreColor(analysis.overallScore)}`}>
                    <span className="score-value">
                      {analysis.overallScore}
                    </span>
                    <span className="score-total">
                      /100
                    </span>
                  </div>
                  <span style={{
                    marginTop: '12px',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: analysis.overallScore >= 85 ? '#d1fae5' : analysis.overallScore >= 70 ? '#dbeafe' : analysis.overallScore >= 50 ? '#fef3c7' : '#fee2e2',
                    color: analysis.overallScore >= 85 ? '#065f46' : analysis.overallScore >= 70 ? '#1e40af' : analysis.overallScore >= 50 ? '#92400e' : '#991b1b'
                  }}>
                    {getScoreLabel(analysis.overallScore)}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={runAnalysis}
                  disabled={analyzing}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    background: analyzing ? '#cbd5e1' : '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    color: '#475569',
                    cursor: analyzing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => !analyzing && (e.currentTarget.style.borderColor = '#667eea')}
                  onMouseLeave={(e) => !analyzing && (e.currentTarget.style.borderColor = '#cbd5e1')}
                >
                  {analyzing ? (
                    <>
                      <span className="spinner" style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid #e2e8f0',
                        borderTop: '2px solid #475569',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        display: 'inline-block'
                      }}></span>
                      Re-analyzing...
                    </>
                  ) : (
                    <>
                      <span>🔄</span>
                      Re-run Analysis
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Detailed Scores */}
            <div style={{
              marginBottom: '24px',
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '16px 20px',
                background: '#f7fafc',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '20px' }}>📊</span>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a202c' }}>Detailed Scoring</h3>
              </div>
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                  {analysis.scores && Object.entries(analysis.scores).map(([key, data], index) => (
                    <div key={index} className="score-item">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '13px', color: '#1a202c', fontWeight: '600' }}>
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </strong>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: data.score >= 85 ? '#d1fae5' : data.score >= 70 ? '#dbeafe' : data.score >= 50 ? '#fef3c7' : '#fee2e2',
                          color: data.score >= 85 ? '#065f46' : data.score >= 70 ? '#1e40af' : data.score >= 50 ? '#92400e' : '#991b1b'
                        }}>
                          {data.score}/100
                        </span>
                      </div>
                      <div style={{
                        height: '8px',
                        background: '#e2e8f0',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${data.score}%`,
                          background: data.score >= 85 ? '#10b981' : data.score >= 70 ? '#3b82f6' : data.score >= 50 ? '#f59e0b' : '#ef4444',
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>
                        {data.feedback}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Strengths */}
            {analysis.strengths && analysis.strengths.length > 0 && (
              <div style={{
                marginBottom: '24px',
                background: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '16px 20px',
                  background: '#d1fae5',
                  borderBottom: '1px solid #6ee7b7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>⭐</span>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#065f46' }}>Strengths</h3>
                </div>
                <div style={{ padding: '20px' }}>
                  <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'none' }}>
                    {analysis.strengths.map((strength, index) => (
                      <li key={index} style={{ marginBottom: '12px', fontSize: '14px', color: '#1a202c', lineHeight: '1.6', position: 'relative', paddingLeft: '24px' }}>
                        <span style={{ position: 'absolute', left: 0, color: '#10b981' }}>✓</span>
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Areas for Improvement */}
            {analysis.areasForImprovement && analysis.areasForImprovement.length > 0 && (
              <div style={{
                marginBottom: '24px',
                background: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '16px 20px',
                  background: '#fef3c7',
                  borderBottom: '1px solid #fde68a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>⚠️</span>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#92400e' }}>Areas for Improvement</h3>
                </div>
                <div style={{ padding: '20px' }}>
                  <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'none' }}>
                    {analysis.areasForImprovement.map((area, index) => (
                      <li key={index} style={{ marginBottom: '12px', fontSize: '14px', color: '#1a202c', lineHeight: '1.6', position: 'relative', paddingLeft: '24px' }}>
                        <span style={{ position: 'absolute', left: 0, color: '#f59e0b' }}>→</span>
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Actionable Suggestions */}
            {analysis.suggestions && analysis.suggestions.length > 0 && (
              <div style={{
                marginBottom: '24px',
                background: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '16px 20px',
                  background: '#dbeafe',
                  borderBottom: '1px solid #93c5fd',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>💡</span>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e40af' }}>Actionable Suggestions</h3>
                </div>
                <div style={{ padding: '20px' }}>
                  <ol style={{ margin: 0, paddingLeft: '20px', counterReset: 'suggestion-counter' }}>
                    {analysis.suggestions.map((suggestion, index) => (
                      <li key={index} style={{ marginBottom: '12px', fontSize: '14px', color: '#1a202c', lineHeight: '1.6', listStyleType: 'decimal' }}>
                        {suggestion}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}

            {/* Detailed Feedback */}
            {analysis.detailedFeedback && (
              <div style={{
                marginBottom: '24px',
                background: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '16px 20px',
                  background: '#f7fafc',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>📄</span>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a202c' }}>Comprehensive Analysis</h3>
                </div>
                <div style={{ padding: '20px' }}>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: '14px', color: '#1a202c' }}>
                    {analysis.detailedFeedback}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'flex-end',
          background: '#ffffff'
        }}>
          <button
            onClick={onHide}
            style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Close
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default TeachingAnalysisModal;
